import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MailCheck, History, RotateCcw } from "lucide-react";
import {
  CommunicationStatusRow,
  useCommunicationLog,
  useSetCommunicationStatus,
} from "@/hooks/useCommunicationStatus";
import CommunicationStatusBadge from "./CommunicationStatusBadge";
import { useToast } from "@/hooks/use-toast";

interface Props {
  entityType: string;
  entityId: string;
  row?: CommunicationStatusRow | null;
  title?: string;
  markSentLabel?: string;
  markPendingLabel?: string;
}

/**
 * Full Communication Status section for use inside an entity's edit dialog.
 * Displays badge + sent metadata + notes + audit log + Mark as Sent/Pending.
 */
const CommunicationStatusPanel = ({
  entityType,
  entityId,
  row,
  title = "Communication Status",
  markSentLabel = "Mark Confirmation Email as Sent",
  markPendingLabel = "Mark as not sent",
}: Props) => {
  const { toast } = useToast();
  const { setStatus } = useSetCommunicationStatus();
  const entries = useCommunicationLog(entityType, entityId);
  const [note, setNote] = useState<string>(row?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const applyStatus = async (next: "sent" | "pending") => {
    setSaving(true);
    const { error } = await setStatus(entityType, entityId, next, {
      note: note.trim() || undefined,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Couldn't save that change", description: "Try again in a moment.", variant: "destructive" });
      return;
    }
    toast({
      title: next === "sent" ? "Marked as sent" : "Marked as not sent",
    });
  };


  const sentAt = row?.sent_at ? new Date(row.sent_at) : null;

  return (
    <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        <CommunicationStatusBadge row={row} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div>
          <div className="uppercase tracking-wide text-[10px]">Date Sent</div>
          <div className="text-foreground">{sentAt ? sentAt.toLocaleDateString() : "—"}</div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[10px]">Time Sent</div>
          <div className="text-foreground">
            {sentAt ? sentAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[10px]">Sent By</div>
          <div className="text-foreground">{row?.sent_by_name || "—"}</div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[10px]">Last Updated</div>
          <div className="text-foreground">
            {row?.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Notes (optional)</Label>
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Sent via personal Gmail with logistics attachment"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {row?.status !== "sent" ? (
          <Button size="sm" onClick={() => applyStatus("sent")} disabled={saving}>
            <MailCheck className="h-4 w-4 mr-1" /> {markSentLabel}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => applyStatus("pending")} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-1" /> {markPendingLabel}
          </Button>
        )}
      </div>

      {entries.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground flex items-center gap-1">
            <History className="h-3 w-3" /> Audit trail ({entries.length})
          </summary>
          <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {entries.map((e) => (
              <li key={e.id} className="border-l-2 border-muted pl-2">
                <div>
                  <span className="font-medium">{e.previous_status ?? "—"}</span> →{" "}
                  <span className="font-medium">{e.new_status}</span>
                </div>
                <div className="text-muted-foreground">
                  {new Date(e.changed_at).toLocaleString()} · {e.changed_by_name || "System"}
                  {e.note ? ` · "${e.note}"` : ""}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default CommunicationStatusPanel;
